import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import ObjectsTree from "./ObjectsTree";
import ToolFlyout, { toolLabel } from "./ToolFlyout";
import { TOOL_ICONS, TOOL_CATEGORIES, UI, categoryOf } from "../../design/icons/ChartIconRegistry";
import { useToolFavorites } from "../../lib/useToolFavorites";

const railBtn = (on, extra = "") => `relative h-8 w-8 rounded-md flex items-center justify-center transition-colors ${on ? "bg-primary text-primary-foreground glow-amber" : `text-muted-foreground hover:bg-secondary hover:text-foreground ${extra}`}`;

// Left tool rail: favourites bar → categorised flyouts (rail button shows the last-used tool of its category) → magnet / lock / hide / objects / clear.
export default function DrawingRail({ active, onSelect, snap, onToggleSnap, drawings, selectedId, onSelectObject, onUpdate, onRemove, onClear, onLockAll, onHideAll, tfId }) {
  const { favorites, toggleFavorite, lastUsed, remember } = useToolFavorites();
  const [open, setOpen] = useState(null);
  const shown = drawings.filter((d) => d.visible).length;
  const allLocked = drawings.length > 0 && drawings.every((d) => d.locked);
  const allHidden = drawings.length > 0 && drawings.every((d) => !d.visible);
  const pick = (id, meta) => { const cat = categoryOf(id); if (cat) remember(cat.id, id); onSelect(id, meta); setOpen(null); };
  const Sep = () => <div className="my-1 h-px w-6 bg-border" />;
  const Magnet = UI.magnet, Lock = allLocked ? UI.lock : UI.unlock, Hide = allHidden ? UI.hide : UI.show, Objects = UI.objects, Trash = UI.trash, Caret = UI.caret;

  return (
    <div className="flex flex-col items-center gap-0.5 py-2 px-1 border-r border-border bg-panel overflow-y-auto scrollbar-thin" data-testid="drawing-rail">
      {favorites.length > 0 && (
        <div className="flex flex-col items-center gap-0.5" data-testid="draw-favorites">
          {favorites.map((id) => { const Icon = TOOL_ICONS[id]; return Icon ? (
            <button key={id} data-testid={`draw-fav-tool-${id}`} title={`★ ${toolLabel(id)}`} onClick={() => pick(id)} className={railBtn(active === id)}>
              <Icon size={16} stroke={1.7} />
              <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
            </button>) : null; })}
          <Sep />
        </div>
      )}
      {TOOL_CATEGORIES.map((cat) => {
        const current = cat.tools.includes(lastUsed[cat.id]) ? lastUsed[cat.id] : cat.tools[0];
        const Icon = TOOL_ICONS[current] ?? cat.icon;
        const on = cat.tools.includes(active);
        return (
          <Popover key={cat.id} open={open === cat.id} onOpenChange={(o) => setOpen(o ? cat.id : null)}>
            <div className="relative group">
              <button data-testid={`draw-cat-${cat.id}`} title={`${cat.label} · ${toolLabel(current)}`} onClick={() => pick(current)} onContextMenu={(e) => { e.preventDefault(); setOpen(cat.id); }}
                className={railBtn(on, cat.layer === "felacoo" ? "text-primary/80" : "")}>
                <Icon size={16} stroke={1.7} />
              </button>
              <PopoverTrigger asChild>
                <button data-testid={`draw-cat-open-${cat.id}`} title={`Open ${cat.label}`} className={`absolute -right-0.5 bottom-0 h-3.5 w-3.5 rounded-sm flex items-center justify-center bg-panel text-muted-foreground hover:text-foreground ${open === cat.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                  <Caret size={9} weight="bold" />
                </button>
              </PopoverTrigger>
            </div>
            <PopoverContent side="right" align="start" sideOffset={6} className="p-0 w-auto bg-popover">
              <ToolFlyout category={cat} active={active} favorites={favorites} onToggleFavorite={toggleFavorite} onPick={pick} />
            </PopoverContent>
          </Popover>
        );
      })}
      <Sep />
      <button data-testid="draw-snap-toggle" title={snap ? "Magnet on: snaps to OHLC, swings, BOS, FVG/OB edges" : "Magnet off"} onClick={onToggleSnap}
        className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors ${snap ? "text-primary bg-primary/15" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
        <Magnet size={16} weight={snap ? "fill" : "regular"} />
      </button>
      <button data-testid="draw-lock-all" title={allLocked ? "Unlock all drawings" : "Lock all drawings"} onClick={() => onLockAll(!allLocked)} className={railBtn(false, allLocked ? "text-primary" : "")}>
        <Lock size={16} />
      </button>
      <button data-testid="draw-hide-all" title={allHidden ? "Show all drawings" : "Hide all drawings"} onClick={() => onHideAll(!allHidden)} className={railBtn(false, allHidden ? "text-primary" : "")}>
        <Hide size={16} />
      </button>
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="draw-objects-btn" title="Object tree" className={railBtn(false)}>
            <Objects size={16} />
            {drawings.length > 0 && <span data-testid="draw-objects-count" className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">{shown}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="w-80 p-0 bg-popover">
          <ObjectsTree drawings={drawings} selectedId={selectedId} onSelect={onSelectObject} onUpdate={onUpdate} onRemove={onRemove} onClear={onClear} tfId={tfId} />
        </PopoverContent>
      </Popover>
      <button data-testid="draw-tool-clear" title="Remove all drawings" onClick={onClear} className={railBtn(false, "hover:!text-sell")}>
        <Trash size={16} />
      </button>
    </div>
  );
}
